import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Image as ImageIcon, Star, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get correct image URL for both local and R2 storage
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  // If URL is absolute (starts with http), use as-is (Cloudflare R2)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise, prepend backend URL (local storage)
  return `${BACKEND_URL}${imageUrl}`;
};

const CATEGORIES = [
  { value: 'training', label: 'Training Sessions' },
  { value: 'events', label: 'Events' },
  { value: 'workshops', label: 'Workshops' },
  { value: 'conferences', label: 'Conferences' },
  { value: 'certification', label: 'Certifications' },
  { value: 'general', label: 'General' }
];

export const Gallery = () => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    event_date: '',
    display_order: 0,
    is_active: true,
    is_featured: false,
  });
  const [newImages, setNewImages] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load gallery events');
    }
  };

  const fetchEventDetail = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch event detail:', error);
      toast.error('Failed to load event details');
      return null;
    }
  };

  const handleImageUpload = async (files) => {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        setUploadingImages(prev => [...prev, file.name]);
        const token = localStorage.getItem('token');
        console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
        const response = await axios.post(
          `${BACKEND_URL}/api/admin/upload/poster`,
          formDataUpload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        console.log('Upload response:', response.data);
        setNewImages(prev => [...prev, { url: response.data.url, caption: '' }]);
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Upload failed:', error);
        console.error('Error response:', error.response?.data);
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
      } finally {
        setUploadingImages(prev => prev.filter(name => name !== file.name));
      }
    }
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        description: event.description || '',
        category: event.category || 'general',
        event_date: event.event_date || '',
        display_order: event.display_order || 0,
        is_active: event.is_active,
        is_featured: event.is_featured || false,
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        event_date: '',
        display_order: 0,
        is_active: true,
        is_featured: false,
      });
    }
    setNewImages([]);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Submit called - newImages:', newImages, 'editingEvent:', editingEvent);

    if (!editingEvent && newImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingEvent) {
        // Update event
        await axios.put(
          `${BACKEND_URL}/api/admin/gallery-events/${editingEvent.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Event updated successfully');
      } else {
        // Create new event with images
        const createData = {
          ...formData,
          image_urls: newImages.map(img => img.url)
        };
        await axios.post(
          `${BACKEND_URL}/api/admin/gallery-events`,
          createData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Event created successfully');
      }

      setIsDialogOpen(false);
      setNewImages([]);
      fetchEvents();
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error('Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete the event and all its images.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/gallery-events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleManageImages = async (event) => {
    const eventDetail = await fetchEventDetail(event.id);
    if (eventDetail) {
      setSelectedEvent(eventDetail);
      setNewImages([]);
      setIsImageDialogOpen(true);
    }
  };

  const handleAddImagesToEvent = async () => {
    if (newImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      for (const img of newImages) {
        await axios.post(
          `${BACKEND_URL}/api/admin/gallery-events/${selectedEvent.id}/images`,
          {
            image_url: img.url,
            caption: img.caption || null,
            display_order: 0
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Images added successfully');
      setNewImages([]);
      
      // Refresh event detail
      const updated = await fetchEventDetail(selectedEvent.id);
      if (updated) {
        setSelectedEvent(updated);
      }
      fetchEvents();
    } catch (error) {
      console.error('Failed to add images:', error);
      toast.error('Failed to add images');
    }
  };

  const handleDeleteImage = async (eventId, imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/gallery-events/${eventId}/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Image deleted');
      
      // Refresh event detail
      const updated = await fetchEventDetail(eventId);
      if (updated) {
        setSelectedEvent(updated);
      }
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('Failed to delete image');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gallery Management</h1>
          <p className="text-gray-600 mt-1">Manage gallery events with multiple photos</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add New Event
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <Card key={event.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg line-clamp-1">{event.title}</CardTitle>
                    {event.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {CATEGORIES.find(c => c.value === event.category)?.label || event.category}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(event)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(event.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {event.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                )}
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">
                    {event.image_count || 0} {event.image_count === 1 ? 'image' : 'images'}
                  </span>
                  {event.event_date && (
                    <span className="text-gray-500">{new Date(event.event_date).toLocaleDateString()}</span>
                  )}
                  <span className={event.is_active ? 'text-green-600' : 'text-red-600'}>
                    {event.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => handleManageImages(event)}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Manage Images
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-12">
          <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No gallery events yet</h3>
          <p className="text-gray-500 mb-4">Start by creating your first gallery event</p>
          <Button onClick={() => handleOpenDialog()}>Create First Event</Button>
        </div>
      )}

      {/* Add/Edit Event Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Edit Gallery Event' : 'Create Gallery Event'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingEvent && (
              <div>
                <label className="text-sm font-semibold text-gray-700">Images *</label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e.target.files)}
                  disabled={uploadingImages.length > 0}
                />
                {uploadingImages.length > 0 && (
                  <p className="text-sm text-blue-600 mt-1">Uploading {uploadingImages.length} file(s)...</p>
                )}
                {newImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {newImages.map((img, idx) => (
                      <div key={idx} className="relative bg-gray-50 rounded-lg p-2">
                        <img
                          src={getImageUrl(img.url)}
                          alt={`Preview ${idx + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-semibold text-gray-700">Event Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., ISO 9001 Training - May 2024"
                required
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                rows={3}
                placeholder="Brief description of the event"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">Event Date</label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Display Order</label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Higher numbers appear first</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="is_active" className="text-sm font-semibold text-gray-700">
                  Active (Show on public gallery)
                </label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="is_featured" className="text-sm font-semibold text-gray-700">
                  Featured (Show on homepage)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingImages.length > 0}>
                {editingEvent ? 'Update Event' : 'Create Event'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Images Dialog */}
      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Images - {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Images Section */}
            <div className="border-b pb-4">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Add New Images</label>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageUpload(e.target.files)}
                disabled={uploadingImages.length > 0}
              />
              {uploadingImages.length > 0 && (
                <p className="text-sm text-blue-600 mt-1">Uploading {uploadingImages.length} file(s)...</p>
              )}
              {newImages.length > 0 && (
                <div className="mt-3">
                  <div className="grid grid-cols-4 gap-3">
                    {newImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={getImageUrl(img.url)}
                          alt={`New ${idx + 1}`}
                          className="w-full h-32 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={handleAddImagesToEvent} 
                    className="mt-3"
                    disabled={uploadingImages.length > 0}
                  >
                    Add {newImages.length} Image(s) to Event
                  </Button>
                </div>
              )}
            </div>

            {/* Existing Images Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Current Images ({selectedEvent?.images?.length || 0})
              </h3>
              {selectedEvent?.images && selectedEvent.images.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {selectedEvent.images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={getImageUrl(img.image_url)}
                        alt={img.caption || 'Gallery image'}
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      {img.caption && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-1">{img.caption}</p>
                      )}
                      <button
                        onClick={() => handleDeleteImage(selectedEvent.id, img.id)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No images yet. Add some above!</p>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setIsImageDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
